/**
 * Analytics — dual sink (GA4 + PostHog), with first-touch UTM attribution.
 *
 * - GA4 fires via window.gtag (loaded from index.html).
 * - PostHog fires via window.posthog (loaded via snippet in index.html).
 *   PostHog only activates when REACT_APP_POSTHOG_KEY is set at build time
 *   AND initAnalytics() is called — missing env => silent no-op.
 *
 * Every event is auto-enriched with:
 *   - timestamp
 *   - first-touch UTM fields (utm_source / medium / campaign / content / term)
 *     so conversion funnels in either tool can attribute by channel.
 */
import { getAttributionEventParams } from "@/utils/utm";

// ========================== Sink getters ==========================
function getGtag() {
  return typeof window !== "undefined" && typeof window.gtag === "function"
    ? window.gtag
    : null;
}

function getPosthog() {
  return typeof window !== "undefined" && window.posthog && typeof window.posthog.capture === "function"
    ? window.posthog
    : null;
}

// ========================== Initialization ==========================
let _posthogInitialized = false;

/**
 * Call once at app start-up. Safe to call repeatedly; only inits PostHog once.
 * Fails silently if REACT_APP_POSTHOG_KEY is missing — GA4 continues working.
 */
export function initAnalytics() {
  if (typeof window === "undefined") return;
  if (_posthogInitialized) return;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
  try {
    if (window.posthog && typeof window.posthog.init === "function") {
      window.posthog.init(key, {
        api_host: host,
        capture_pageview: true,
        persistence: "localStorage+cookie",
        autocapture: false, // we instrument manually to stay signal-clean
      });
      _posthogInitialized = true;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] PostHog init failed:", e?.message);
  }
}

/** Identify the authenticated user to PostHog (called post-login). */
export function identifyUser({ id, role, email, method }) {
  if (!id) return;
  const ph = getPosthog();
  if (!ph) return;
  try {
    ph.identify(id, {
      role: role || "student",
      email: email || undefined,
      signup_method: method || undefined,
    });
  } catch (_) { /* ignore */ }
}

// ========================== Core tracker ==========================
function trackEvent(eventName, params = {}) {
  const enriched = {
    ...getAttributionEventParams(),
    ...params,
    timestamp: new Date().toISOString(),
  };

  const gtag = getGtag();
  if (gtag) {
    try { gtag("event", eventName, enriched); } catch (_) { /* ignore */ }
  }
  const ph = getPosthog();
  if (ph) {
    try { ph.capture(eventName, enriched); } catch (_) { /* ignore */ }
  }
}

// ========================== Event helpers ==========================
export function trackUserSignup({ role, method }) {
  trackEvent("user_signup", {
    user_role: role || "student",
    signup_method: method || "email",
  });
}

export function trackUserLogin({ role, method, className }) {
  trackEvent("user_login", {
    user_role: role || "student",
    login_method: method || "email",
    class_name: className || undefined,
  });
}

export function trackClassJoined({ role, className, classCode }) {
  trackEvent("class_joined", {
    user_role: role || "student",
    class_name: className || undefined,
    class_code: classCode || undefined,
  });
}

export function trackGameStarted({ role, moduleName, gameMode }) {
  trackEvent("game_started", {
    user_role: role || "student",
    module_name: moduleName || undefined,
    game_mode: gameMode || "solo",
  });
}

export function trackModuleCompleted({ role, moduleName, sessionNumber }) {
  trackEvent("module_completed", {
    user_role: role || "student",
    module_name: moduleName || undefined,
    session_number: sessionNumber || undefined,
  });
}

export function trackAssessmentCompleted({ role, moduleName, assessmentType }) {
  trackEvent("assessment_completed", {
    user_role: role || "student",
    module_name: moduleName || undefined,
    assessment_type: assessmentType || "session_assessment",
  });
}

export function trackBusinessPlanCompleted({ role }) {
  trackEvent("business_plan_completed", {
    user_role: role || "student",
  });
}

// ---- Landing-page conversion events (added May 2026) ----
export function trackLandingView({ page }) {
  trackEvent("landing_view", { page: page || "landing" });
}

export function trackLandingCTAClick({ page, ctaId, ctaLabel, destination }) {
  trackEvent("landing_cta_click", {
    page: page || "landing",
    cta_id: ctaId || undefined,
    cta_label: ctaLabel || undefined,
    destination: destination || undefined,
  });
}

export function trackLandingScrollDepth({ page, depth }) {
  // `depth` is an integer like 25/50/75/100
  trackEvent("landing_scroll_depth", { page: page || "landing", depth });
}

export function trackRegisterButtonClick({ page, role }) {
  trackEvent("register_button_click", {
    page: page || "landing",
    user_role: role || undefined,
  });
}

// ---- Crash reporting ----
export function trackError({ name, message, scope }) {
  trackEvent("frontend_error", {
    error_name: name || "UnknownError",
    error_message: message || "",
    scope: scope || "root",
  });
}
